// Built-ins
import React from "react";
import { useState } from "react";

// Icons / Images
import { BiPlus } from "react-icons/bi";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";

// Modules / Functions
import Swal from "sweetalert2";
import { Modal, Form } from "react-bootstrap";
import GuardedModal from "../../common/GuardedModal";
import { Button } from "semantic-ui-react";
import axios from "axios";

// Global Variables
import { headers } from "../../global_vars";
import { useMergedModalVisibility } from "../../common/useMergedModalVisibility";
import { getNestedModalProps } from "../../common/nestedModalProps";

// Variables
window.Swal = Swal;

// API endpoint for creating professions
const CREATE_PROFESSION = "https://ultima.icsgr.com/api/administration/all_professions/";

// Helpers
const clampLen = (value, max) => value.slice(0, max);

// Validation helpers
const validateTitle = (value) => value.replace(/[^a-zA-Z0-9ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩαβγδεζηθικλμνξοπρστυφχψωάέήίόύώΐΰΆΈΉΊΌΎΏ\s\-']/g, "");

export default function AddProfessionModal({
  onProfessionCreated,
  show: showProp,
  onHide: onHideProp,
  hideTrigger = false,
  nested = false,
  stackLevel = 1,
}) {
  const { show, open, close } = useMergedModalVisibility(showProp, onHideProp);
  const [formData, setFormData] = useState({
    title: "",
  });

  const handleClose = () => {
    close();
    setFormData({
      title: "",
    });
  };

  const handleShow = () => open();

  const isFormValid = () => {
    return (
      formData.title.trim().length >= 2 &&
      formData.title.trim().length <= 40
    );
  };

  const createNewProfession = async () => {
    if (!isFormValid()) {
      Swal.fire({
        icon: "error",
        title: "Invalid Form",
        text: "Please fill in all required fields correctly.",
      });
      return;
    }

    try {
      const currentHeaders = {
        ...headers,
        "Authorization": "Token " + localStorage.getItem("userToken")
      };

      const payload = {
        title: formData.title.trim(),
      };

      const res = await axios.post(CREATE_PROFESSION, payload, { headers: currentHeaders });

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Profession created successfully!",
      });

      if (typeof onProfessionCreated === "function") {
        onProfessionCreated(res.data);
      }
      handleClose();

      if (typeof onProfessionCreated !== "function") {
        const newId = res.data.profession_id;
        window.location.href = `/administration/profession/${newId}`;
      }
      
    } catch (e) {
      console.log('Error creating profession:', e);
      
      let apiMsg = "Failed to create profession.";
      
      if (e?.response?.data?.error) {
        apiMsg = e.response.data.error;
      } else if (e?.response?.data?.title) {
        apiMsg = e.response.data.title[0];
      } else if (typeof e?.response?.data === 'object') {
        try {
          const all = Object.values(e.response.data).flat().join(' ');
          if (all) apiMsg = all;
        } catch (_ignored) {}
      } else if (e?.response?.data?.detail) {
        apiMsg = e.response.data.detail;
      }
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: apiMsg,
      });
    }
  };

  return (
    <>
      {!hideTrigger && (
        <Button onClick={handleShow} color="green" style={{ margin: 20 }}>
          <BiPlus style={{ marginRight: 6 }} />
          New Profession
        </Button>
      )}

      <GuardedModal
        show={show}
        onHide={handleClose}
        centered
        size="lg"
        {...(nested ? getNestedModalProps(stackLevel) : {})}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create New Profession</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title:</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  title: validateTitle(clampLen(e.target.value, 40))
                }))}
                placeholder="e.g., Lawyer"
                isInvalid={
                  formData.title !== "" &&
                  (formData.title.trim().length < 2 || formData.title.trim().length > 40)
                }
              />
              <Form.Control.Feedback type="invalid">
                Title must be 2-40 characters
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid() ? (
              <ul
                className="mr-auto"
                style={{ margin: 0, padding: 0, color: "red" }}
              >
                {formData.title.trim().length < 2 && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Title is required (2–40 chars).
                  </li>
                )}
                {formData.title.trim().length > 40 && (
                  <li>
                    <AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />
                    Title must be 2–40 chars.
                  </li>
                )}
              </ul>
            ) : (
              <div style={{ color: "green" }}>
                <AiOutlineCheckCircle style={{ fontSize: 18, marginRight: 6 }} />
                All fields are valid!
              </div>
            )}
          </small>
          <Button color="red" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={createNewProfession}
            disabled={!isFormValid()}
          >
            Create Profession
          </Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}
